const CSV_URL = 'https://docs.google.com/spreadsheets/d/e/2PACX-1vStnTuQq3es0pkcIDzlQYVKA-pUiQWrhJ5Phw-goYL23HLlBA6Cs527QmE3OP37_nnn4Ezsigo-u06e/pub?output=csv';

export const DEFAULT_WORD_CIRCLES = [
  ['Light', 'Flame', 'Burn', 'Mark', 'Note', 'Key', 'Solution', 'Dissolve', 'Melt'],
  ['Rifle', 'Gun', 'Crank', 'Grouch', 'Grouse', 'Turkey', 'Loser', 'Sack'],
  ['Wave', 'Roller', 'Pin', 'Secure', 'Guard', 'Muzzle', 'Mute', 'Dumb', 'Dull', 'Soft', 'Tender', 'Bid'],
  ['Stoop', 'Sink', 'Scuttle', 'Scramble', 'Mix', 'Fuse', 'Join', 'Adopt', 'Angle', 'Bend'],
  ['Book', 'Page', 'Leaf', 'Tree', 'Wood', 'Log', 'Record', 'Album'],
];

interface WordListData {
  Date: string;
  WordList: string;
  [key: string]: string;
}

// Custom CSV parser that handles quoted fields
function parseCSV(text: string): string[][] {
  const rows: string[][] = [];
  let row: string[] = [];
  let value = '';
  let insideQuotes = false;

  for (let i = 0; i < text.length; i++) {
    const char = text[i];
    const nextChar = text[i + 1];

    if (char === '"' && insideQuotes && nextChar === '"') {
      // Handle escaped quotes
      value += '"';
      i++; // Skip the next quote
    } else if (char === '"') {
      insideQuotes = !insideQuotes;
    } else if (char === ',' && !insideQuotes) {
      row.push(value.trim());
      value = '';
    } else if ((char === '\n' || char === '\r') && !insideQuotes) {
      if (value !== '' || row.length > 0) {
        row.push(value.trim());
        value = '';
        rows.push(row);
        row = [];
      }
      // Skip '\r' if next char is '\n'
      if (char === '\r' && nextChar === '\n') {
        i++;
      }
    } else {
      value += char;
    }
  }

  // Add the last value
  if (value !== '' || row.length > 0) {
    row.push(value.trim());
    rows.push(row);
  }

  return rows;
}

// Get today's date in YYYY-MM-DD format
function getTodayKey(): string {
  const today = new Date();
  const year = today.getFullYear();
  const month = (today.getMonth() + 1).toString().padStart(2, '0');
  const day = today.getDate().toString().padStart(2, '0');
  return `${year}-${month}-${day}`;
}

// Parse CSV date in M/D/YYYY format to YYYY-MM-DD
function parseCSVDate(dateStr: string): string | null {
  const parts = dateStr.split('/');
  if (parts.length === 3) {
    const month = parts[0].padStart(2, '0');
    const day = parts[1].padStart(2, '0');
    const year = parts[2];
    return `${year}-${month}-${day}`;
  }
  return null;
}

// Fetch word list from CSV
async function fetchWordListData(): Promise<WordListData[] | null> {
  try {
    const response = await fetch(CSV_URL);
    const csvText = await response.text();
    const rows = parseCSV(csvText);
    const headers = rows[0];
    
    const data = rows.slice(1).map(row => {
      const obj: WordListData = { Date: '', WordList: '' };
      headers.forEach((header, index) => {
        obj[header] = row[index] || '';
      });
      return obj;
    });
    
    return data;
  } catch (error) {
    return null;
  }
}

// Get word list for today's date
function getWordListForToday(data: WordListData[]): string[] {
  const key = getTodayKey();
  
  // Find today's data
  for (const row of data) {
    const csvDate = parseCSVDate(row.Date);
    if (csvDate === key && row.WordList) {
      return row.WordList.split(',').map(word => word.trim()).filter(word => word.length > 0);
    }
  }
  
  // If no data for today, return a deterministic default circle based on date
  const dateHash = getTodayKey().split('-').reduce((sum, part) => sum + parseInt(part), 0);
  const circleIndex = dateHash % DEFAULT_WORD_CIRCLES.length;
  return DEFAULT_WORD_CIRCLES[circleIndex];
}

// Get word list for a specific date
function getWordListForDate(data: WordListData[], date: string): string[] {
  const csvDate = parseCSVDate(date);
  if (!csvDate) return DEFAULT_WORD_CIRCLES[0];
  
  for (const row of data) {
    const rowDate = parseCSVDate(row.Date);
    if (rowDate === csvDate && row.WordList) {
      return row.WordList.split(',').map(word => word.trim()).filter(word => word.length > 0);
    }
  }
  
  return DEFAULT_WORD_CIRCLES[0];
}

// Main function to get today's word list
export async function getTodaysWordList(): Promise<string[]> {
  try {
    const data = await fetchWordListData();
    if (data) {
      return getWordListForToday(data);
    }
  } catch (error) {
  }
  
  // Fallback to deterministic default circle based on today's date
  const dateHash = getTodayKey().split('-').reduce((sum, part) => sum + parseInt(part), 0);
  const circleIndex = dateHash % DEFAULT_WORD_CIRCLES.length;
  return DEFAULT_WORD_CIRCLES[circleIndex];
}

// Function to get a random word circle for testing
export function getRandomWordCircle(): string[] {
  const randomIndex = Math.floor(Math.random() * DEFAULT_WORD_CIRCLES.length);
  return DEFAULT_WORD_CIRCLES[randomIndex];
}

// Function to get all default word circles
export function getAllDefaultCircles(): string[][] {
  return DEFAULT_WORD_CIRCLES;
}